// GiftCardModal.jsx
// ★ GIFT CARD FOR SOMEONE ELSE (owner request 2026-10-22). Opened from the
//   BUYER's client profile. Staff enter who the gift is for, the amount paid
//   and the credit value, then charge the buyer with the normal Charge or
//   Record window — so the payment and its receipt belong to the BUYER. Once
//   paid, the credit is added to the RECIPIENT's wallet (a new client is
//   created if she isn't in the system) and the sale is stored in gift_cards.

import { useState } from "react";
import { supabase } from "./supabase";
import { ChargeModal } from "./StripePayment";
import { RecordPaymentModal } from "./SalesReport";

const C = {
  surface:"#ffffff", border:"#e8e4df", gold:"#b48c3c", goldD:"#8a6f2e", goldBg:"rgba(180,140,60,0.08)",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)", red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  text:"#1a1a2e", muted:"#6b7280", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";
const inp = { width:"100%", padding:"10px 12px", background:"#fff", border:"1px solid "+C.border, borderRadius:8, fontSize:14, color:C.text, fontFamily:F, outline:"none", boxSizing:"border-box" };
const lbl = { fontSize:10, fontWeight:700, color:C.gold, textTransform:"uppercase", letterSpacing:"0.05em", display:"block", marginBottom:5 };

const last10 = (p) => String(p || "").replace(/\D/g, "").slice(-10);
const newCode = () => {
  const part = () => Math.random().toString(36).slice(2, 6).toUpperCase();
  return `NOVA-${part()}-${part()}`;
};

export default function GiftCardModal({ buyer, onClose, onDone }) {
  const [rName, setRName] = useState("");
  const [rPhone, setRPhone] = useState("");
  const [rEmail, setREmail] = useState("");
  const [paid, setPaid] = useState("");
  const [value, setValue] = useState("");
  const [message, setMessage] = useState("");
  const [match, setMatch] = useState(null);       // existing recipient client
  const [step, setStep] = useState("form");      // form | charge | record | saving | done
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const amountPaid = +paid || 0;
  const creditValue = +value || amountPaid;
  const description = `Gift Card for ${rName.trim() || "recipient"} — $${creditValue.toFixed(2)} value`;

  const findRecipient = async () => {
    const p10 = last10(rPhone);
    const e = rEmail.trim().toLowerCase();
    let c = null;
    if (p10.length === 10) {
      const { data } = await supabase.from("clients").select("id,name,phone,email").like("phone", `%${p10}%`).limit(1);
      c = data?.[0] || null;
    }
    if (!c && e) {
      const { data } = await supabase.from("clients").select("id,name,phone,email").ilike("email", e).limit(1);
      c = data?.[0] || null;
    }
    setMatch(c && c.id !== buyer.id ? c : null);
    return c && c.id !== buyer.id ? c : null;
  };

  const validate = () => {
    if (!rName.trim()) return "Enter who the gift card is for.";
    if (!rPhone.trim() && !rEmail.trim()) return "Enter the recipient's phone or email so the credit can be found later.";
    if (amountPaid <= 0) return "Enter the amount the buyer pays.";
    if (creditValue < amountPaid) return "Gift value can't be less than the amount paid.";
    return "";
  };
  const startPayment = async (method) => {
    const v = validate();
    if (v) { setError(v); return; }
    setError("");
    await findRecipient();
    setStep(method);
  };

  // Payment went through → recipient wallet credit + gift card record.
  const finish = async (payment) => {
    setStep("saving");
    try {
      const { data: { user } } = await supabase.auth.getUser();
      let staff = "Staff";
      if (user?.id) {
        const { data: p } = await supabase.from("profiles").select("full_name,email").eq("id", user.id).maybeSingle();
        staff = p?.full_name || p?.email || "Staff";
      }

      let recipient = match || await findRecipient();
      if (!recipient) {
        const created = {
          id: Date.now(), name: rName.trim(), phone: rPhone.trim(), email: rEmail.trim().toLowerCase() || null,
          dob: null, allergies: "None", med: "No contraindications", spent: 0, visits: 0, noshow: 0, tags: ["gift card"],
          notes: `Received a gift card from ${buyer.name}`, status: "active", cardOnFile: false, gfeStatus: "none",
          loyaltyPts: 0, memberPlan: null, loc: "Nova Med Spa",
        };
        const { error: ce } = await supabase.from("clients").insert([created]);
        if (ce) throw new Error("Could not create the recipient: " + ce.message);
        recipient = created;
      }

      const code = newCode();
      const today = new Date().toISOString().slice(0, 10);
      const { data: pkg, error: pe } = await supabase.from("patient_packages").insert([{
        client_id: recipient.id, client_name: recipient.name,
        package_name: "Gift Card", treatment_type: "Gift Card",
        total_sessions: 0, used_sessions: 0, price_paid: amountPaid,
        dollar_balance: creditValue, unit_value: 0, is_credit: true, status: "active",
        purchase_date: today,
        notes: `Gift from ${buyer.name} · paid $${amountPaid.toFixed(2)} on ${today} · ${code}${message.trim() ? " · \"" + message.trim() + "\"" : ""}`,
      }]).select("id").single();
      if (pe) throw new Error("Payment went through, but the credit could not be added: " + pe.message);

      await supabase.from("wallet_log").insert([{
        client_id: recipient.id, client_name: recipient.name, patient_package_id: pkg.id,
        action_type: "credit", item_name: "Gift Card",
        description: `Gift Card from ${buyer.name} - $${creditValue.toFixed(2)} (${code})`,
        units_delta: 0, dollar_delta: creditValue, employee_name: staff,
      }]);

      await supabase.from("gift_cards").insert([{
        code, buyer_client_id: buyer.id, buyer_name: buyer.name,
        recipient_client_id: recipient.id, recipient_name: recipient.name,
        recipient_phone: rPhone.trim() || recipient.phone || null, recipient_email: rEmail.trim() || recipient.email || null,
        amount_paid: amountPaid, credit_value: creditValue, message: message.trim() || null,
        payment_id: payment?.id || null, patient_package_id: pkg.id, created_by_name: staff,
      }]);

      setResult({ code, recipient });
      setStep("done");
      onDone && onDone();
    } catch (e) {
      setError(e.message || String(e));
      setStep("form");
    }
  };

  if (step === "charge") {
    return <ChargeModal client={buyer} defaultAmount={amountPaid} description={description}
      onClose={() => setStep("form")} onComplete={(p) => finish(p)} />;
  }
  if (step === "record") {
    return <RecordPaymentModal client={buyer} defaultAmount={amountPaid} description={description}
      onClose={() => setStep("form")} onComplete={(p) => finish(p)} />;
  }

  return (
    <div onClick={step === "saving" ? undefined : onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background:"#fff", borderRadius:14, width:"100%", maxWidth:480, maxHeight:"92vh", overflowY:"auto", padding:22, fontFamily:F, position:"relative" }}>
        <button onClick={onClose} style={{ position:"absolute", top:12, right:12, background:C.light, border:"none", borderRadius:7, padding:"4px 10px", cursor:"pointer", fontSize:16, color:C.muted }}>×</button>
        <div style={{ fontSize:19, fontWeight:800, color:C.gold, fontFamily:S }}>🎁 Gift Card</div>

        {step === "done" ? (
          <div style={{ marginTop:14 }}>
            <div style={{ padding:14, background:C.greenL, borderRadius:10, color:C.green, fontWeight:700, fontSize:14, marginBottom:12 }}>✓ Gift card created</div>
            <div style={{ fontSize:13, color:C.text, lineHeight:1.7 }}>
              <div><strong>${creditValue.toFixed(2)}</strong> credit added to <strong>{result.recipient.name}</strong>'s wallet.</div>
              <div>Paid by <strong>{buyer.name}</strong> — the receipt went to the buyer.</div>
              <div>Code: <strong style={{ fontFamily:"monospace" }}>{result.code}</strong></div>
            </div>
            <button onClick={onClose} style={{ marginTop:16, width:"100%", padding:"11px", background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:F }}>Done</button>
          </div>
        ) : (
          <>
            <div style={{ fontSize:12, color:C.muted, margin:"2px 0 16px" }}>
              Bought by <strong style={{ color:C.text }}>{buyer.name}</strong> — they pay and get the receipt. The credit goes to the person below.
            </div>

            <div style={{ fontSize:12, fontWeight:800, color:C.text, marginBottom:8 }}>Who is it for?</div>
            <div style={{ marginBottom:10 }}><label style={lbl}>Recipient name</label><input value={rName} onChange={e => setRName(e.target.value)} placeholder="Jane Smith" style={inp} /></div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:6 }}>
              <div><label style={lbl}>Phone</label><input value={rPhone} onChange={e => { setRPhone(e.target.value); setMatch(null); }} onBlur={findRecipient} type="tel" placeholder="(555) 123-4567" style={inp} /></div>
              <div><label style={lbl}>Email</label><input value={rEmail} onChange={e => { setREmail(e.target.value); setMatch(null); }} onBlur={findRecipient} type="email" autoCapitalize="none" placeholder="optional" style={inp} /></div>
            </div>
            <div style={{ fontSize:11, color: match ? C.green : C.muted, marginBottom:14 }}>
              {match ? `✓ Existing client: ${match.name} — the credit goes to her wallet` : "If she isn't a client yet, a profile is created for her."}
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
              <div><label style={lbl}>Buyer pays ($)</label><input type="number" inputMode="decimal" value={paid} onChange={e => setPaid(e.target.value)} placeholder="250" style={inp} /></div>
              <div><label style={lbl}>Gift value ($)</label><input type="number" inputMode="decimal" value={value} onChange={e => setValue(e.target.value)} placeholder={paid || "same as paid"} style={inp} /></div>
            </div>
            <div style={{ marginBottom:14 }}><label style={lbl}>Message (optional)</label><input value={message} onChange={e => setMessage(e.target.value)} placeholder="Happy birthday! 💛" style={inp} /></div>

            {error && <div style={{ padding:"10px 12px", background:C.redL, color:C.red, borderRadius:8, fontSize:12, fontWeight:700, marginBottom:12 }}>{error}</div>}
            {step === "saving" ? (
              <div style={{ padding:12, textAlign:"center", color:C.muted, fontSize:13 }}>Saving the gift card…</div>
            ) : (
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={() => startPayment("charge")} style={{ flex:1, padding:"11px", background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:F }}>💳 Charge card</button>
                <button onClick={() => startPayment("record")} style={{ flex:1, padding:"11px", background:C.goldBg, color:C.goldD, border:"1px solid "+C.gold, borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:F }}>💵 Cash / other</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
